import React, { useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize  from '@mui/material/TextareaAutosize';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import ScatterPlot from './ScatterPlot'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import toFixed from '../lib/toFixed'
import csvToObject from '../lib/csv'
import EquationFitter from '../lib/equation-finder'
import { linearRegression } from '../lib/stats'

const EquationFinder = () => {
  const [ data, setData ] = useAutoSaveState('data', '')
  const [ inputs, setInputs ] = useAutoSaveState('input', '')
  const [ output, setOutput ] = useAutoSaveState('output', '')
  const [ , setPreview ] = useAutoSaveState('preview', null)
  const [ result, setResult ] = useState('')
  const [ scatterPlot, setScatterPlot ] = useState(null)
  const [isCalculating, setIsCalculating] = useState(false);
  const fitterRef = useRef(null);
  const stopRef = useRef(false);

  useEffect(() => {
    const sanitize = label => label.trim().replace(/[^a-zA-Z0-9]/g, '_')
    const inputVars = inputs.split(',').map(sanitize).filter(v => v)
    const outputVar = sanitize(output.trim())
    let arr
    try {
      arr = JSON.parse(data)
    } catch (error) {
      arr = csvToObject(data, sanitize)
    }

    if (!arr || !arr.length || !outputVar || !inputVars.length) return;

    fitterRef.current = new EquationFitter(arr, inputVars, outputVar);
    stopRef.current = true;
    setIsCalculating(false);
    setResult(null);
    setScatterPlot(null);
  }, [data, inputs, output]);

  const calculate = async () => {
    if (isCalculating) {
      // Stop the calculation
      stopRef.current = true;
      setIsCalculating(false);
      return;
    }

    // Start the calculation
    stopRef.current = false;
    setIsCalculating(true);

    while (!stopRef.current) {
      const fitter = fitterRef.current;
      if (!fitter) return;

      const best = fitter.findBestEquation(100);
      if (!best || !best.length) return;

      if (best && best.length && !result || !result.best || result.best[0].simplified !== best[0].simplified) {
        const points = fitter.yTrue.map((y, i) => ({ x: y, y: best[0].data[i] }));
        const regression = linearRegression(points);

        setResult({
          actual: fitter.yTrue,
          best: best
        });

        setScatterPlot({
          points,
          regression
        });

        setPreview(best[0].simplified)
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      await new Promise(resolve => setTimeout(resolve, 5));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Equation Finder
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Inputs" autoFocus value={inputs} onChange={e => setInputs(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Output" autoFocus value={output} onChange={e => setOutput(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">CSV Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data}
                  onChange={e => setData(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>
                {isCalculating ? 'Stop' : 'Calculate'}
              </Button>
            </Grid>
          </Paper>
        </Grid>
        {scatterPlot && <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <ScatterPlot data={scatterPlot.points} regressionLine={{ m: scatterPlot.regression.m, b: scatterPlot.regression.b }} />
          </Paper>
        </Grid>}
        {result && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Top Equations
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>R<sup>2</sup></TableCell>
                        <TableCell>Avg Error</TableCell>
                        <TableCell>Least Square Error</TableCell>
                        <TableCell>Equation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.best.map(row => (
                        <TableRow
                          key={row.simplified}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell align="right">{toFixed(row.r2, 3)}</TableCell>
                          <TableCell align="right">{toFixed(row.avgError, 3)}</TableCell>
                          <TableCell align="right">{toFixed(row.error, 3)}</TableCell>
                          <TableCell align="right"><code style={{ fontSize: 12 }}>{output} = {row.simplified}</code></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
            </Box>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
};

export default function EquationFinderWrapper () {
  return <AutoSaveProvider>
    <EquationFinder />
  </AutoSaveProvider>
}
